import React, { useState, useEffect } from "react";
import { auth, provider } from "../services/firebase-config";
import { getAuth, GoogleAuthProvider, OAuthProvider, FacebookAuthProvider, signInWithPopup, signInWithEmailAndPassword, signInWithCredential, signInWithCustomToken } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { postRequest } from "../utils/api";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Grid2,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Google as GoogleIcon, Facebook as FacebookIcon, Apple as AppleIcon } from "@mui/icons-material";
import { gapi } from "gapi-script";
import useAuth from "../hooks/useAuth"; // Ensure it's imported
import { t } from "@lingui/macro";
import GoogleLogo from "../logos/google-logo.png";
import AppleLogo from "../logos/apple-logo.png";
import FacebookLogo from "../logos/facebook-logo.png";


const loadAppleSDK = () => {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const loadFacebookSDK = () => {
  return new Promise((resolve, reject) => {
    // Check if SDK is already loaded
    if (window.FB) {
      console.log("Facebook SDK already loaded.");
      return resolve();
    }

    // Create a script element to load the SDK
    const script = document.createElement("script");
    script.src = "https://connect.facebook.net/en_US/sdk.js";
    script.async = true;
    script.defer = true;

    // Set up the event handlers
    script.onload = () => {
      window.FB.init({
        appId: process.env.REACT_APP_FACEBOOK_APP_ID, // Replace with your Facebook App ID
        autoLogAppEvents: true,
        xfbml: false, // Set to true if you use social plugins
        version: "v16.0", // Replace with the desired Facebook API version
      });
      console.log("Facebook SDK initialized.");
      resolve();
    };
    script.onerror = () => {
      console.error("Failed to load Facebook SDK.");
      reject(new Error("Failed to load Facebook SDK."));
    };

    // Append the script to the document head
    document.head.appendChild(script);
  });
};

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ type: "", message: "" });
  const [openDialog, setOpenDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorCode, setErrorCode] = useState("");
  const navigate = useNavigate();
  const { user, refetchAuth } = useAuth(true); // Force fresh auth fetch on mount

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification({ type: "", message: "" }), 3000);
  };

useEffect(() => {
  const loadGapi = () => {
    if (!window.gapi?.auth2?.getAuthInstance()) {
      gapi.load("auth2", () => {
        gapi.auth2.init({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        });
      });
    }
  };

  loadGapi();
}, []);



  const initGapi = () => {
    return new Promise((resolve, reject) => {
      gapi.load("auth2", () => {
        gapi.auth2
          .init({
            client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          })
          .then(resolve, reject);
      });
    });
  };

  const handleAuth = async (authAction, onSuccessNavigate = false) => {
    setLoading(true);
    try {
      await authAction();
      showNotification("success", t`Login successful!`);
      if (onSuccessNavigate) navigate("/"); // Only navigate if explicitly instructed
    } catch (error) {

      setErrorMessage(error.message || t`Login failed.`);
      setErrorCode(error.code || "");
      setOpenDialog(true);

      console.error("Error during authentication:", error);

      showNotification("error", error.message || t`Login failed.`);
    } finally {
      setLoading(false);
    }
    await refetchAuth();
  };

  const redirectIfNotEditor = async () => {
    const authInstance = getAuth();
    const user = authInstance.currentUser;
    if (!user) return;
    const idTokenResult = await user.getIdTokenResult();
    await refetchAuth();
    if (!idTokenResult.claims.isEditor) {
      navigate("/mystories");
    } else {
      navigate("/"); // or another route for editors
    }
  };

  const handleGoogleLogin = () =>
    handleAuth(async () => {
      try {
        // await initGapi(); removed as it is already called in useEffect
        const googleAuth = gapi.auth2.getAuthInstance();
        const googleUser = await googleAuth.signIn();
        const googleIdToken = googleUser.getAuthResponse().id_token;

        const response = await postRequest("authEditorFunctions-googleLogin", { idToken: googleIdToken });

        if (!response.success) {
          const error = new Error(response.message || t`Login failed.`);
          error.code = response.code || ""; // manual extra field
          throw error;
        }

        const credential = GoogleAuthProvider.credential(googleIdToken);
        await signInWithCredential(auth, credential);

        console.log("Google Login Success");

        await redirectIfNotEditor();

      } catch (error) {
        console.error("Google Login Error:", error.message);
        setErrorMessage(error.message || t`An unexpected error occurred.`);
        setErrorCode(error.code || "");
        setOpenDialog(true);
        throw error;
      }
    });

  const handleFirebaseGoogleSignIn = () =>
    handleAuth(async () => {
      const googleProvider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, googleProvider);
      console.log("Firebase Google User Signed In:", result.user);
    });

  const handleFirebaseAppleSignIn = () =>
    handleAuth(async () => {
      const auth = getAuth();
      const appleProvider = new OAuthProvider("apple.com");

      // Optionally add scopes (e.g., email, name)
      appleProvider.addScope("email");
      appleProvider.addScope("name");

      // Trigger the sign-in popup
      const result = await signInWithPopup(auth, appleProvider);
      console.log("Firebase Apple User Signed In:", result.user);
    });

  const handleFirebaseFacebookSignIn = () =>
    handleAuth(async () => {
      const facebookProvider = new FacebookAuthProvider();
      const result = await signInWithPopup(auth, facebookProvider);
      console.log("Firebase Facebook User Signed In:", result.user);
    });

  const handleAppleLogin = () =>
    handleAuth(async () => {
      try {
        // Load Apple SDK dynamically
        await loadAppleSDK();
        if (!window.AppleID) throw new Error("AppleID SDK not loaded");

        // Initialize Apple sign-in
        window.AppleID.auth.init({
          clientId: process.env.REACT_APP_APPLE_CLIENT_ID, // Replace with your client ID
          scope: "name email", // Scopes
          redirectURI: process.env.REACT_APP_APPLE_REDIRECT_URI, // Redirect URI
          usePopup: true, // Use popup to avoid redirections
        });

        // Trigger Apple sign-in popup
        const response = await window.AppleID.auth.signIn();
        const idToken = response.authorization.id_token;
        const rawNonce = response.authorization.nonce || null;

        // Send `idToken` to backend and get `customToken`
        const backendResponse = await postRequest(
          "authEditorFunctions-appleLogin", // Replace with environment variable or constant if possible
          { idToken, rawNonce }
        );
        if (!backendResponse.success) {
          const error = new Error(backendResponse.message || t`Login failed.`);
          error.code = backendResponse.code || ""; // manual extra field
          throw error;
        }

        // Use `customToken` to authenticate with Firebase
        const auth = getAuth(); // Ensure `auth` is properly initialized
        const customToken = backendResponse.token;
        const firebaseUserCredential = await signInWithCustomToken(auth, customToken);
        const firebaseUser = firebaseUserCredential.user;

        console.log("Firebase User UID:", firebaseUser.uid);
        console.log("Firebase User Email:", firebaseUser.email);
        console.log("Apple Login and Firebase Authentication Successful.");

        await redirectIfNotEditor();

      } catch (error) {
        console.error("Apple Login Error:", error.message || error);
        setErrorMessage(error.message || t`An unexpected error occurred.`);
        setErrorCode(error.code || "");
        setOpenDialog(true);
        throw error;
      }
    });

  const handleFacebookLogin = () =>
    handleAuth(async () => {
      try {
        await loadFacebookSDK();

        if (!window.FB) {
          throw new Error("Facebook SDK not loaded");
        }

        const authResponse = await new Promise((resolve, reject) => {
          window.FB.login(
            (response) => {
              if (response.authResponse) {
                resolve(response.authResponse);
              } else {
                reject(new Error("Facebook login canceled or failed."));
              }
            },
            { scope: "public_profile,email" }
          );
        });

        await processFacebookLogin(authResponse);
      } catch (error) {
        console.error("Facebook Login Error:", error.message || error);

        const errorMsg = error.message.includes("register")
          ? t`Account not found. Please register in the app. Use the links below to download the app.`
          : error.message || t`An unexpected error occurred.`;

        setErrorMessage(error.message || t`An unexpected error occurred.`);
        setErrorCode(error.code || "");
        setTimeout(() => setOpenDialog(true), 0);
        throw error; // Let `handleAuth` handle state updates
      }
    }, true); // Enable navigation after success

  const processFacebookLogin = async (authResponse) => {
    try {
      const { accessToken } = authResponse;

      const backendResponse = await postRequest("authEditorFunctions-facebookLogin", { accessToken });

      if (!backendResponse.success) {
        const error = new Error(backendResponse.message || t`Login failed.`);
        error.code = backendResponse.code || ""; // manual extra field
        throw error;
      }

      const auth = getAuth();
      const customToken = backendResponse.token;
      const firebaseUserCredential = await signInWithCustomToken(auth, customToken);
      const firebaseUser = firebaseUserCredential.user;

      console.log("Firebase User UID:", firebaseUser.uid);
      console.log("Firebase User Email:", firebaseUser.email);
      console.log("Facebook Login and Firebase Authentication Successful.");

      await redirectIfNotEditor();

    } catch (error) {
      console.error("Error processing Facebook login:", error.message || error);
      throw error; // Re-throw to let parent functions handle it
    }
  };

  const handleEmailLogin = () =>
    handleAuth(async () => {
      const response = await postRequest("authEditorFunctions-emailLogin", { email, password });
      if (!response.success) {
        const error = new Error(response.message || t`Login failed.`);
        error.code = response.code || ""; // manual extra field
        throw error;
      }

      const auth = getAuth();
      const customToken = response.token;
      const firebaseUserCredential = await signInWithCustomToken(auth, customToken);
      const firebaseUser = firebaseUserCredential.user;

      console.log("Firebase User UID:", firebaseUser.uid);
      console.log("Firebase User Email:", firebaseUser.email);

      // Refresh the ID token to get the latest custom claims
      await firebaseUser.getIdToken(true);

      // Fetch the updated ID token and extract custom claims
      const idTokenResult = await firebaseUser.getIdTokenResult();
      console.log("Updated Custom Claims:", idTokenResult.claims);

      if (idTokenResult.claims.isEditor) {
        console.log("User is an editor!");
      } else {
        console.log("User is a regular user.");
      }

      await redirectIfNotEditor();
    });

  const handleDialogClose = () => setOpenDialog(false);

  return (
    <Box
      sx={{
        width: 350,
        margin: "auto",
        textAlign: "center",
        mt: 4,
        p: 3,
        border: "1px solid #ccc",
        borderRadius: 2,
        position: "relative",
      }}
    >
      <Typography variant="h5" gutterBottom>
        {t`Login to Continue`}
      </Typography>

      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            zIndex: 1,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <Grid2
        container
        spacing={2}
        justifyContent="center"
        sx={{
          opacity: loading ? 0.5 : 1,
          mt: 2,
          mb: 2,
          "& > .MuiGrid2-root": { // Ensure all buttons are the same size
            width: 56,
            height: 56,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }
        }}
      >
        {/* Apple Button */}
        <Grid2 item>
          <Button
            variant="contained"
            onClick={handleAppleLogin}
            sx={{
              width: 56,
              height: 56,
              minWidth: 56,
              borderRadius: "50%",
              backgroundColor: "#fff",
              color: "inherit",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              border: "1px solid #ccc",
              boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
              overflow: "hidden", // Prevents anything from expanding outside
              "&:hover": {
                backgroundColor: "#f5f5f5"
              },
            }}
          >
            <AppleIcon sx={{ fontSize: 38, color: "black" }} />
          </Button>
        </Grid2>

        {/* Facebook Button */}
        <Grid2 item>
          <Button
            variant="contained"
            onClick={handleFacebookLogin}
            sx={{
              width: 56,
              height: 56,
              minWidth: 56,
              borderRadius: "50%",
              backgroundColor: "transparent",
              color: "#fff",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",

              overflow: "hidden", // Prevents stretching issues
              "&:hover": { backgroundColor: "#155bc0" },
            }}
          >
            <img
              src={FacebookLogo} // Ensure this is a transparent PNG
              alt="Facebook logo"
              style={{ width: 56, height: 56, objectFit: "contain" }}
            />
          </Button>
        </Grid2>

        {/* Google Button */}
        <Grid2 item>
          <Button
            variant="contained"
            onClick={handleGoogleLogin}
            sx={{
              width: 56,
              height: 56,
              minWidth: 56,
              borderRadius: "50%",
              backgroundColor: "#fff",
              color: "inherit",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              border: "1px solid #ccc",
              boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
              overflow: "hidden",
              "&:hover": { backgroundColor: "#f5f5f5" },
            }}
          >
            <img
              src={GoogleLogo}
              alt="Google logo"
              style={{ width: 36, height: 36, objectFit: "contain" }}
            />
          </Button>
        </Grid2>
      </Grid2>


      {/* <Grid2 container spacing={2} justifyContent="center" sx={{ opacity: loading ? 0.5 : 1 }}>
        <Grid2 item xs={12}>
          <Button
            variant="contained"
            fullWidth
            startIcon={<GoogleIcon />}
            onClick={handleFirebaseGoogleSignIn}
            sx={{
              backgroundColor: "#34A853",
              color: "#fff",
              "&:hover": { backgroundColor: "#2c913e" },
              textTransform: "none",
              fontWeight: "bold",
            }}
          >
            {t`Firebase Google Login (Test)`}
          </Button>
        </Grid2>
        <Grid2 item xs={12}>
          <Button
            variant="contained"
            fullWidth
            startIcon={<AppleIcon />}
            onClick={handleFirebaseAppleSignIn}
            sx={{
              backgroundColor: "#000",
              color: "#fff",
              "&:hover": { backgroundColor: "#333" },
              textTransform: "none",
              fontWeight: "bold",
            }}
          >
            {t`Firebase Apple Login (test)`}
          </Button>
        </Grid2>
        <Grid2 item xs={12}>
          <Button
            variant="contained"
            fullWidth
            startIcon={<FacebookIcon />}
            onClick={handleFirebaseFacebookSignIn}
            sx={{
              backgroundColor: "#4267B2",
              color: "#fff",
              "&:hover": { backgroundColor: "#3b5998" },
              textTransform: "none",
              fontWeight: "bold",
            }}
          >
            {t`Firebase Facebook Login (Test)`}
          </Button>
        </Grid2>
      </Grid2> */}

      <Divider sx={{ my: 2 }}>{t`or`}</Divider>

      <Box
        component="form"
        noValidate
        autoComplete="off"
        sx={{ opacity: loading ? 0.5 : 1 }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && email.trim() && password.trim()) {
            e.preventDefault(); // Prevent form submission refresh
            handleEmailLogin();
          }
        }}
      >

        <TextField
          variant="outlined"
          fullWidth
          margin="normal"
          label={t`Email`}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextField
          variant="outlined"
          fullWidth
          margin="normal"
          label={t`Password`}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Button
          variant="contained"
          fullWidth
          onClick={() => handleEmailLogin()}
          sx={{ mt: 2 }}
          disabled={!email.trim() || !password.trim()}
        >
          {t`Login with Email`}
        </Button>

      </Box>

      <Dialog open={openDialog} onClose={handleDialogClose}>
        <DialogTitle>
          {errorCode === "registration_incomplete"
            ? t`Registration Incomplete`
            : errorCode === "account_not_found"
              ? t`Account Not Found`
              : t`Login Error`
          }
        </DialogTitle>


        <DialogContent>  {errorCode === "registration_incomplete" ? (
          <Typography variant="body2" gutterBottom>
            {t`Please complete your registration inside the app, then try logging in again.`}
          </Typography>
        ) : errorCode === "account_not_found"
          ? (
            <Typography variant="body2" gutterBottom>
              {t`Please register in the app first to proceed. Download the app here:`}
            </Typography>
          ) : errorCode === "invalid_email" ? (
            <Typography variant="body2" gutterBottom>
              {t`That email address doesn’t look right. Please check and try again.`}
            </Typography>
          ) : errorCode === "invalid_password" ? (
            <Typography variant="body2" gutterBottom>
              {t`The password you entered is incorrect. Please try again.`}
            </Typography>
          ) : errorCode === "account_disabled" ? (
            <Typography variant="body2" gutterBottom>
              {t`Your account has been disabled. Please contact support.`}
            </Typography>
          ) : errorCode === "unknown_error" ? (
            <Typography variant="body2" gutterBottom>
              {errorMessage}
            </Typography>
          ) : (
            <Typography variant="body1" gutterBottom>
              {t`An unexpected error occurred. Please try again.`}
            </Typography>
          )}

          {(errorCode === "registration_incomplete" || errorCode === "account_not_found") && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                mt: 2,
              }}
            >
              {/* Google Play Button */}
              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{
                  backgroundColor: "#0F9D58",
                  "&:hover": { backgroundColor: "#0C7B47" },
                  padding: "10px",
                }}
                component="a"
                href="https://play.google.com/store/apps/details?id=com.TwentyThreeGames.KitaplikProto"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                  alt={t`Google Play Store`}
                  style={{ height: 40 }}
                />
              </Button>

              {/* Apple App Store Button */}
              <Button
                variant="contained"
                color="default"
                fullWidth
                sx={{
                  backgroundColor: "#000",
                  "&:hover": { backgroundColor: "#333" },
                  padding: "10px",
                }}
                component="a"
                href="https://apps.apple.com/fi/app/hitreads-hikaye-oyunu-kitap/id6450996377"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                  alt={t`Apple App Store`}
                  style={{ height: 40 }}
                />
              </Button>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            {t`Close`}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!notification.message} autoHideDuration={3000} onClose={() => setNotification({ type: "", message: "" })}>
        <Alert severity={notification.type} sx={{ width: "100%" }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Login;
